/**
 * Spotify Web API Service
 *
 * Handles authorization, search, and library operations
 * for the Spotify Web API using PKCE OAuth flow.
 *
 * @see https://developer.spotify.com/documentation/web-api
 */

import {
  generatePKCEPair,
  storePKCEVerifier,
  retrievePKCEVerifier,
} from '../utils/pkce.js';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const SPOTIFY_CLIENT_ID = import.meta.env.VITE_SPOTIFY_CLIENT_ID;
const SPOTIFY_REDIRECT_URI = import.meta.env.VITE_SPOTIFY_REDIRECT_URI || `${window.location.origin}`;

const SPOTIFY_SCOPES = 'user-library-modify user-read-private';
const SPOTIFY_AUTH_URL = 'https://accounts.spotify.com/authorize';
const SPOTIFY_API_BASE = 'https://api.spotify.com/v1';

// Token storage keys
const TOKEN_STORAGE_KEY = 'spotify_tokens';

// Internal state
let accessToken = null;
let refreshToken = null;
let tokenExpiry = null;

/**
 * Initialize Spotify service
 *
 * Loads any stored tokens from localStorage.
 * Unlike MusicKit, Spotify doesn't have a JS SDK to load.
 *
 * @returns {Promise<void>}
 */
export async function initializeSpotify() {
  console.log('[Spotify] Initializing...');
  loadStoredTokens();
  console.log('[Spotify] Initialized', { hasToken: !!accessToken });
}

/**
 * Load stored tokens from localStorage
 */
function loadStoredTokens() {
  try {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      accessToken = data.accessToken || null;
      refreshToken = data.refreshToken || null;
      tokenExpiry = data.tokenExpiry ? new Date(data.tokenExpiry) : null;

      // Clear expired tokens
      if (tokenExpiry && tokenExpiry < new Date()) {
        console.log('[Spotify] Stored token expired, will need to refresh');
        // Keep refresh token, clear access token
        accessToken = null;
      }
    }
  } catch (error) {
    console.error('[Spotify] Failed to load stored tokens:', error);
  }
}

/**
 * Save tokens to localStorage
 */
function saveTokens() {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify({
      accessToken,
      refreshToken,
      tokenExpiry: tokenExpiry?.toISOString(),
    }));
  } catch (error) {
    console.error('[Spotify] Failed to save tokens:', error);
  }
}

/**
 * Clear all stored tokens
 */
function clearTokens() {
  accessToken = null;
  refreshToken = null;
  tokenExpiry = null;
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

/**
 * Check if user is currently authorized
 *
 * @returns {boolean}
 */
export function isAuthorized() {
  if (!accessToken) return false;
  if (tokenExpiry && tokenExpiry < new Date()) {
    // Token expired but we might have refresh token
    return !!refreshToken;
  }
  return true;
}

/**
 * Check if we have a valid (non-expired) access token
 *
 * @returns {boolean}
 */
export function hasValidAccessToken() {
  if (!accessToken) return false;
  if (tokenExpiry && tokenExpiry < new Date()) return false;
  return true;
}

/**
 * Get the Spotify authorization URL
 *
 * Generates PKCE challenge and stores verifier for later use.
 *
 * @returns {Promise<string>} The authorization URL
 */
export async function getAuthUrl() {
  const { verifier, challenge } = await generatePKCEPair();
  storePKCEVerifier(verifier);

  const params = new URLSearchParams({
    client_id: SPOTIFY_CLIENT_ID,
    response_type: 'code',
    redirect_uri: SPOTIFY_REDIRECT_URI,
    scope: SPOTIFY_SCOPES,
    code_challenge_method: 'S256',
    code_challenge: challenge,
  });

  return `${SPOTIFY_AUTH_URL}?${params.toString()}`;
}

/**
 * Start the authorization flow
 *
 * Redirects the user to Spotify's authorization page.
 *
 * @returns {Promise<void>}
 */
export async function authorize() {
  const authUrl = await getAuthUrl();
  console.log('[Spotify] Redirecting to authorization...');
  window.location.href = authUrl;
}

/**
 * Handle the OAuth callback
 *
 * Exchanges the authorization code for access and refresh tokens.
 *
 * @param {string} code - The authorization code from the callback
 * @returns {Promise<boolean>} True if successful
 */
export async function handleCallback(code) {
  const codeVerifier = retrievePKCEVerifier();

  if (!codeVerifier) {
    throw new Error('PKCE verifier not found. Please try authorizing again.');
  }

  console.log('[Spotify] Exchanging authorization code...');

  const response = await fetch(`${API_URL}/api/spotify/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      code,
      code_verifier: codeVerifier,
      redirect_uri: SPOTIFY_REDIRECT_URI,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(`Token exchange failed: ${error.error || response.statusText}`);
  }

  const data = await response.json();

  accessToken = data.access_token;
  refreshToken = data.refresh_token;
  tokenExpiry = new Date(Date.now() + (data.expires_in * 1000));

  saveTokens();
  console.log('[Spotify] Authorization successful');

  return true;
}

/**
 * Refresh the access token using the refresh token
 *
 * @returns {Promise<boolean>} True if successful
 */
export async function refreshAccessToken() {
  if (!refreshToken) {
    throw new Error('No refresh token available. Please authorize again.');
  }

  console.log('[Spotify] Refreshing access token...');

  const response = await fetch(`${API_URL}/api/spotify/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });

  if (!response.ok) {
    // Refresh failed - clear tokens and require re-auth
    clearTokens();
    throw new Error('Token refresh failed. Please authorize again.');
  }

  const data = await response.json();

  accessToken = data.access_token;
  // Spotify may return a new refresh token
  if (data.refresh_token) {
    refreshToken = data.refresh_token;
  }
  tokenExpiry = new Date(Date.now() + (data.expires_in * 1000));

  saveTokens();
  console.log('[Spotify] Token refreshed successfully');

  return true;
}

/**
 * Ensure we have a valid access token, refreshing if needed
 *
 * @returns {Promise<string>} The valid access token
 */
async function ensureValidToken() {
  if (!accessToken && refreshToken) {
    await refreshAccessToken();
  } else if (tokenExpiry && tokenExpiry < new Date() && refreshToken) {
    await refreshAccessToken();
  }

  if (!accessToken) {
    throw new Error('Not authorized. Please connect to Spotify first.');
  }

  return accessToken;
}

/**
 * Sign out the user (revoke authorization)
 *
 * @returns {Promise<void>}
 */
export async function unauthorize() {
  clearTokens();
  console.log('[Spotify] User signed out');
}

/**
 * Search Spotify catalog for tracks
 *
 * @param {string} query - Search query (typically "title artist")
 * @param {number} limit - Maximum results to return (1-50)
 * @returns {Promise<Array>} Array of track objects from Spotify catalog
 */
export async function searchTracks(query, limit = 10) {
  if (!query || query.trim().length === 0) {
    return [];
  }

  const token = await ensureValidToken();

  const params = new URLSearchParams({
    q: query.trim(),
    type: 'track',
    limit: Math.min(limit, 50).toString(),
  });

  const response = await fetch(`${SPOTIFY_API_BASE}/search?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401) {
    // Token expired, try to refresh and retry
    console.log('[Spotify] Token expired, refreshing...');
    await refreshAccessToken();
    return searchTracks(query, limit);
  }

  if (response.status === 429) {
    const retryAfter = response.headers.get('Retry-After') || '1';
    throw new Error(`Rate limited. Retry after ${retryAfter} seconds.`);
  }

  if (!response.ok) {
    console.error('[Spotify] Search failed:', response.status);
    return [];
  }

  const data = await response.json();
  const tracks = data.tracks?.items || [];

  console.log(`[Spotify] Search "${query.substring(0, 30)}..." -> ${tracks.length} results`);

  if (tracks.length > 0) {
    console.log(`[Spotify] Top result ID: ${tracks[0].id} - "${tracks[0].name}" by ${tracks[0].artists?.[0]?.name}`);
  }

  return tracks;
}

/**
 * Save tracks to user's "Your Music" library
 *
 * Maximum 50 tracks per request.
 *
 * @param {string[]} trackIds - Array of Spotify track IDs
 * @returns {Promise<boolean>} True if successful
 */
export async function saveTracks(trackIds) {
  if (!trackIds || trackIds.length === 0) {
    console.log('[Spotify] No tracks to save');
    return true;
  }

  if (trackIds.length > 50) {
    throw new Error('Maximum 50 tracks per request. Use saveTracksBatched for larger lists.');
  }

  const token = await ensureValidToken();

  const response = await fetch(`${SPOTIFY_API_BASE}/me/tracks?ids=${trackIds.join(',')}`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}` },
  });

  if (response.status === 401) {
    console.log('[Spotify] Token expired, refreshing...');
    await refreshAccessToken();
    return saveTracks(trackIds);
  }

  if (response.status === 429) {
    const retryAfter = response.headers.get('Retry-After') || '1';
    return { ok: false, retryAfter: Number(retryAfter) };
  }

  if (!response.ok) {
    console.error('[Spotify] saveTracks failed:', response.status);
    throw new Error(`Failed to save tracks: ${response.statusText}`);
  }

  return true;
}

/**
 * Save tracks to library in batches with rate limiting
 *
 * @param {string[]} trackIds - Array of track IDs to save
 * @param {object} options - Options for batch processing
 * @param {number} options.batchSize - Tracks per batch (default: 50)
 * @param {number} options.delayMs - Delay between batches in ms (default: 200)
 * @param {function} options.onProgress - Progress callback
 * @returns {Promise<{success: number, failed: number}>}
 */
export async function saveTracksBatched(trackIds, options = {}) {
  const {
    batchSize = 50,
    delayMs = 200,
    onProgress,
  } = options;

  let success = 0;
  let failed = 0;

  for (let i = 0; i < trackIds.length; i += batchSize) {
    const batch = trackIds.slice(i, i + batchSize);

    try {
      const result = await saveTracks(batch);

      // Handle rate limit response
      if (result && result.retryAfter) {
        console.log(`[Spotify] Rate limited, waiting ${result.retryAfter}s...`);
        await new Promise(resolve => setTimeout(resolve, result.retryAfter * 1000));
        // Retry this batch
        i -= batchSize;
        continue;
      }

      success += batch.length;
    } catch (error) {
      console.error(`[Spotify] Batch ${Math.floor(i / batchSize) + 1} failed:`, error);
      failed += batch.length;
    }

    if (onProgress) {
      onProgress({
        processed: Math.min(i + batch.length, trackIds.length),
        total: trackIds.length,
        success,
        failed,
      });
    }

    // Add delay between batches to respect rate limits
    if (i + batchSize < trackIds.length) {
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }

  return { success, failed };
}

/**
 * Get current user's profile
 *
 * @returns {Promise<Object>} User profile object
 */
export async function getCurrentUser() {
  const token = await ensureValidToken();

  const response = await fetch(`${SPOTIFY_API_BASE}/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error('Failed to get user profile');
  }

  return response.json();
}
